import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface UserProfile {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  discoverable: boolean;
  onboarding_completed?: boolean | null;
  onboarding_completed_at?: string | null;
  beta_participant?: boolean | null;
  beta_joined_at?: string | null;
  beta_completed?: boolean | null;
  beta_completed_at?: string | null;
}

/**
 * A profile is "complete" (i.e. onboarding is done) when the server-side
 * `onboarding_completed` flag is true, OR — as a fallback for legacy
 * profiles created before that column existed — when both display_name and
 * avatar_url are set. Username is optional but recommended.
 */
export function isProfileComplete(p: UserProfile | null): boolean {
  if (!p) return false;
  if (p.onboarding_completed === true) return true;
  // Legacy fallback: a display name is enough to consider onboarding done.
  // Avatar/username are social-identity nice-to-haves and must not gate the
  // dashboard for returning users (open beta requirement).
  return Boolean(p.display_name?.trim());
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select(
        "id, display_name, username, avatar_url, discoverable, onboarding_completed, onboarding_completed_at, beta_participant, beta_joined_at, beta_completed, beta_completed_at",
      )
      .eq("id", user.id)
      .maybeSingle();
    setProfile((data as UserProfile) ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, refetch: fetchProfile, complete: isProfileComplete(profile) };
}
